import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  approvePetChangeRequest,
  cancelOwnPetChangeRequest,
  createPetChangeRequest,
  fetchOwnPetChangeRequests,
  fetchWorkspacePetChangeRequests,
  rejectPetChangeRequest,
} from '@/api/petChangeRequests/petChangeRequestsApi';
import type {
  PetChangeRequest,
  PetChangeRequestFieldValues,
} from '@/api/petChangeRequests/petChangeRequestsApi.types';
import {
  buildOwnPetChangeRequestsQueryKey,
  buildPatientQueryKey,
  buildWorkspacePetChangeRequestsQueryKey,
  OWN_PET_CHANGE_REQUESTS_QUERY_KEY,
  OWN_PORTAL_SUMMARY_QUERY_KEY,
  WORKSPACE_ATTENTION_QUERY_KEY,
  WORKSPACE_PET_CHANGE_REQUESTS_QUERY_KEY,
} from '@/constants/queryKeys';
import { PET_CHANGE_REQUEST_STATUS } from '@/constants/petChangeRequests';

const PENDING_PET_CHANGE_REQUEST_PAGE_SIZE = 1;

function firstItemOrNull(page: { items: PetChangeRequest[] }): PetChangeRequest | null {
  const first = page.items[0];
  return first === undefined ? null : first;
}

export function usePendingOwnPetChangeRequestQuery(
  patientId: string
): UseQueryResult<PetChangeRequest | null, Error> {
  return useQuery({
    queryKey: buildOwnPetChangeRequestsQueryKey(patientId, PET_CHANGE_REQUEST_STATUS.pending),
    queryFn: async function load() {
      const page = await fetchOwnPetChangeRequests(
        patientId,
        PET_CHANGE_REQUEST_STATUS.pending,
        1,
        PENDING_PET_CHANGE_REQUEST_PAGE_SIZE
      );
      return firstItemOrNull(page);
    },
    enabled: patientId.length > 0,
  });
}

function invalidatePetChangeRequestViews(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string
) {
  queryClient.invalidateQueries({ queryKey: OWN_PET_CHANGE_REQUESTS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: WORKSPACE_PET_CHANGE_REQUESTS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: WORKSPACE_ATTENTION_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: buildPatientQueryKey(patientId) });
}

interface CreatePetChangeRequestVariables {
  patientId: string;
  changes: PetChangeRequestFieldValues;
}

export function useCreatePetChangeRequestMutation(): UseMutationResult<
  PetChangeRequest,
  Error,
  CreatePetChangeRequestVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function create(variables: CreatePetChangeRequestVariables) {
      return createPetChangeRequest(variables.patientId, variables.changes);
    },
    onSuccess: function invalidate(createdRequest) {
      invalidatePetChangeRequestViews(queryClient, createdRequest.patientId);
      queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function useCancelOwnPetChangeRequestMutation(): UseMutationResult<
  PetChangeRequest,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelOwnPetChangeRequest,
    onSuccess: function invalidate(cancelledRequest) {
      invalidatePetChangeRequestViews(queryClient, cancelledRequest.patientId);
      queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function usePendingWorkspacePetChangeRequestQuery(
  patientId: string
): UseQueryResult<PetChangeRequest | null, Error> {
  return useQuery({
    queryKey: buildWorkspacePetChangeRequestsQueryKey(patientId, PET_CHANGE_REQUEST_STATUS.pending),
    queryFn: async function load() {
      const page = await fetchWorkspacePetChangeRequests(
        patientId,
        PET_CHANGE_REQUEST_STATUS.pending,
        1,
        PENDING_PET_CHANGE_REQUEST_PAGE_SIZE
      );
      return firstItemOrNull(page);
    },
    enabled: patientId.length > 0,
  });
}

export function useApprovePetChangeRequestMutation(): UseMutationResult<
  PetChangeRequest,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: approvePetChangeRequest,
    onSuccess: function invalidate(approvedRequest) {
      invalidatePetChangeRequestViews(queryClient, approvedRequest.patientId);
    },
  });
}

interface RejectPetChangeRequestVariables {
  requestId: string;
  reviewNote: string;
}

export function useRejectPetChangeRequestMutation(): UseMutationResult<
  PetChangeRequest,
  Error,
  RejectPetChangeRequestVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function reject(variables: RejectPetChangeRequestVariables) {
      return rejectPetChangeRequest(variables.requestId, variables.reviewNote);
    },
    onSuccess: function invalidate(rejectedRequest) {
      invalidatePetChangeRequestViews(queryClient, rejectedRequest.patientId);
    },
  });
}
